/* Phase 6D — Pending-for-Invoice report + Reconciliation (Feb 2026) */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Loader2, AlertTriangle, RefreshCw, Search, CheckCircle2, XCircle, ClipboardList } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Build the last 12 months + current month as ["YYYY-MM", "MMM YYYY"] pairs.
function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const label = d.toLocaleString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
    opts.push({ value: `${yyyy}-${mm}`, label });
  }
  return opts;
}

function currentMonthStr() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function PendingForInvoiceReport() {
  // Filters (shared across both tabs).
  const [filters, setFilters] = useState({
    vendor_id: '', branch: '', buyer_sku_id: '', month: currentMonthStr(),
  });
  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tab, setTab] = useState('report');

  // Report tab state
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reconciliation tab state
  const [reconRows, setReconRows] = useState([]);
  const [reconTotals, setReconTotals] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  // Drill-down dialog state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillContext, setDrillContext] = useState(null);   // {sku, branch}
  const [drillPayload, setDrillPayload] = useState(null);

  const monthOptions = useMemo(() => buildMonthOptions(), []);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchFilters = useCallback(async () => {
    try {
      const [br, vd] = await Promise.all([
        axios.get(`${API}/api/branches`, { headers: getHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/vendors`, { headers: getHeaders() }).catch(() => ({ data: [] })),
      ]);
      setBranches(Array.isArray(br.data) ? br.data : (br.data?.items || []));
      setVendors(Array.isArray(vd.data) ? vd.data : (vd.data?.items || []));
    } catch (_e) { /* non-fatal */ }
  }, []);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
    if (filters.branch) params.append('branch', filters.branch);
    if (filters.buyer_sku_id) params.append('buyer_sku_id', filters.buyer_sku_id);
    if (filters.month) params.append('month', filters.month);
    return params;
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/fg-inward/pending-for-invoice-report?${buildParams()}`,
        { headers: getHeaders() },
      );
      setRows(r.data.rows || []);
      setTotals(r.data.totals || null);
    } catch (_e) { toast.error('Failed to fetch pending-for-invoice report'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchReconciliation = useCallback(async () => {
    setReconLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/fg-inward/pending-for-invoice-reconciliation?${buildParams()}`,
        { headers: getHeaders() },
      );
      setReconRows(r.data.rows || []);
      setReconTotals(r.data.totals || null);
    } catch (_e) { toast.error('Failed to fetch reconciliation'); }
    setReconLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => { if (tab === 'reconciliation') fetchReconciliation(); }, [tab, fetchReconciliation]);

  const openDrillDown = async (sku, branch) => {
    setDrillContext({ sku, branch });
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillPayload(null);
    try {
      const params = new URLSearchParams({ buyer_sku_id: sku, branch });
      const r = await axios.get(
        `${API}/api/fg-inward/pending-for-invoice-reconciliation/drilldown?${params}`,
        { headers: getHeaders() },
      );
      setDrillPayload(r.data);
    } catch (_e) { toast.error('Failed to fetch drill-down'); }
    setDrillLoading(false);
  };

  return (
    <div className="p-6 md:p-8" data-testid="pending-for-invoice-page">
      <div className="mb-8 flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase">Pending for Invoice</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            Vendor-site FG produced but not yet invoiced.
          </p>
          <p className="text-xs text-amber-700 mt-2 font-medium" data-testid="indicative-disclaimer">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" strokeWidth={2} />
            Indicative Value is informational only — not owned, not in accounting totals.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => (tab === 'reconciliation' ? fetchReconciliation() : fetchReport())}
          data-testid="pending-refresh-btn"
          disabled={loading || reconLoading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${(loading || reconLoading) ? 'animate-spin' : ''}`} strokeWidth={1.5} />
          Refresh
        </Button>
      </div>

      {/* Shared filters */}
      <div className="mb-6 flex flex-wrap gap-3">
        <Select value={filters.vendor_id || '_all'} onValueChange={(v) => setFilters((f) => ({ ...f, vendor_id: v === '_all' ? '' : v }))}>
          <SelectTrigger className="w-[240px]" data-testid="pending-vendor-filter">
            <SelectValue placeholder="All Vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Vendors</SelectItem>
            {vendors.map((v) => (
              <SelectItem key={v.id} value={v.id}>{v.name || v.vendor_name || v.id}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.branch || '_all'} onValueChange={(v) => setFilters((f) => ({ ...f, branch: v === '_all' ? '' : v }))}>
          <SelectTrigger className="w-[200px]" data-testid="pending-branch-filter">
            <SelectValue placeholder="All Branches" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Branches</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Filter by Buyer SKU ID"
            value={filters.buyer_sku_id}
            onChange={(e) => setFilters((f) => ({ ...f, buyer_sku_id: e.target.value }))}
            className="pl-10 w-[260px] font-mono"
            data-testid="pending-sku-filter"
          />
        </div>
        <Select
          value={filters.month || 'all'}
          onValueChange={(v) => setFilters((f) => ({ ...f, month: v === 'all' ? 'all' : v }))}
        >
          <SelectTrigger className="w-[200px]" data-testid="pending-month-filter">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {monthOptions.map((m) => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4" data-testid="pending-tabs">
          <TabsTrigger value="report" data-testid="pending-tab-report">Report</TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="pending-tab-reconciliation">Reconciliation</TabsTrigger>
        </TabsList>

        {/* ── Report tab — flat table (AC6) ─────────────────────────── */}
        <TabsContent value="report">
          {totals && (
            <div className="mb-6 grid grid-cols-3 gap-4" data-testid="pending-grand-totals">
              <div className="border border-zinc-200 bg-white rounded-sm p-4">
                <div className="text-xs font-mono uppercase text-zinc-500">Rows</div>
                <div className="text-2xl font-bold tabular-nums">{Number(totals.rows || 0).toLocaleString()}</div>
              </div>
              <div className="border border-zinc-200 bg-white rounded-sm p-4">
                <div className="text-xs font-mono uppercase text-zinc-500">Pending Qty</div>
                <div className="text-2xl font-bold tabular-nums text-rose-600">{Number(totals.pending_qty || 0).toLocaleString()}</div>
              </div>
              <div className="border border-amber-200 bg-amber-50 rounded-sm p-4" title="Informational only — not accounting value.">
                <div className="text-xs font-mono uppercase text-amber-800">Indicative Value (info only)</div>
                <div className="text-2xl font-bold tabular-nums text-amber-900">
                  ₹ {Number(totals.indicative_value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center p-12 text-zinc-500 font-mono text-sm">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading...
            </div>
          ) : rows.length === 0 ? (
            <div className="border border-dashed border-zinc-200 rounded-sm p-12 text-center text-zinc-500 font-mono text-sm" data-testid="pending-empty">
              No pending-for-invoice intakes match the filters.
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Vendor</th>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Branch</th>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">SKU</th>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">SKU Name</th>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Intake Date</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Pending Qty</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Agreed Price</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-amber-700 uppercase tracking-wider">Indicative Value</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Age (days)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.movement_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`pending-row-${r.movement_id}`}>
                      <td className="p-3 align-middle text-sm">{r.vendor_name || r.vendor_id || '—'}</td>
                      <td className="p-3 align-middle text-sm">{r.branch}</td>
                      <td className="p-3 align-middle font-mono text-sm font-bold">{r.buyer_sku_id}</td>
                      <td className="p-3 align-middle text-sm text-zinc-600 max-w-[260px]">
                        <span className="truncate block" title={r.sku_name || ''}>{r.sku_name || '-'}</span>
                      </td>
                      <td className="p-3 align-middle text-sm font-mono">{r.intake_date ? new Date(r.intake_date).toLocaleDateString() : '-'}</td>
                      <td className="p-3 align-middle text-right font-mono font-semibold text-rose-600">{Number(r.pending_qty).toLocaleString()}</td>
                      <td className="p-3 align-middle text-right font-mono">
                        {r.agreed_price == null ? <span className="text-amber-700" title="No agreed price on file">—</span> : `₹ ${Number(r.agreed_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                      </td>
                      <td className="p-3 align-middle text-right font-mono text-amber-700">
                        {r.indicative_value == null ? '—' : `₹ ${Number(r.indicative_value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                      </td>
                      <td className={`p-3 align-middle text-right font-mono font-semibold ${(r.age_days || 0) > 30 ? 'text-rose-700' : 'text-zinc-700'}`}>{r.age_days ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ── Reconciliation tab (AC7–AC12) ─────────────────────────── */}
        <TabsContent value="reconciliation">
          {reconTotals && (
            <div className="mb-6 grid grid-cols-2 gap-4" data-testid="recon-totals">
              <div className="border border-zinc-200 bg-white rounded-sm p-4">
                <div className="text-xs font-mono uppercase text-zinc-500">Rows</div>
                <div className="text-2xl font-bold tabular-nums">{Number(reconTotals.rows || 0).toLocaleString()}</div>
              </div>
              <div className="border border-rose-200 bg-rose-50 rounded-sm p-4">
                <div className="text-xs font-mono uppercase text-rose-800">Mismatches</div>
                <div className="text-2xl font-bold tabular-nums text-rose-900">{Number(reconTotals.mismatches || 0).toLocaleString()}</div>
              </div>
            </div>
          )}

          {reconLoading ? (
            <div className="text-center p-12 text-zinc-500 font-mono text-sm">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading...
            </div>
          ) : reconRows.length === 0 ? (
            <div className="border border-dashed border-zinc-200 rounded-sm p-12 text-center text-zinc-500 font-mono text-sm" data-testid="recon-empty">
              No rows match the filters.
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Vendor</th>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Branch</th>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">SKU</th>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">SKU Name</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Total Produced</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Invoiced Qty</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Pending (Movement)</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Pending (Inventory)</th>
                    <th className="h-9 px-3 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Match / Δ</th>
                    <th className="h-9 px-3 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Audit</th>
                  </tr>
                </thead>
                <tbody>
                  {reconRows.map((r) => (
                    <tr key={`${r.branch}::${r.buyer_sku_id}`} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`recon-row-${r.branch}-${r.buyer_sku_id}`}>
                      <td className="p-3 align-middle text-sm">{r.vendor_name || r.vendor_id || '—'}</td>
                      <td className="p-3 align-middle text-sm">{r.branch}</td>
                      <td className="p-3 align-middle font-mono text-sm font-bold">{r.buyer_sku_id}</td>
                      <td className="p-3 align-middle text-sm text-zinc-600 max-w-[260px]">
                        <span className="truncate block" title={r.sku_name || ''}>{r.sku_name || '-'}</span>
                      </td>
                      <td className="p-3 align-middle text-right font-mono">{Number(r.total_produced_qty || 0).toLocaleString()}</td>
                      <td className="p-3 align-middle text-right font-mono">{Number(r.invoiced_qty || 0).toLocaleString()}</td>
                      <td className="p-3 align-middle text-right font-mono">{Number(r.pending_by_movement || 0).toLocaleString()}</td>
                      <td className="p-3 align-middle text-right font-mono">{Number(r.pending_by_inventory || 0).toLocaleString()}</td>
                      <td className="p-3 align-middle text-sm">
                        {r.match ? (
                          <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-50 text-emerald-800 px-2 py-0.5 font-mono text-xs" data-testid={`recon-match-${r.branch}-${r.buyer_sku_id}`}>
                            <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> match
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-sm bg-rose-50 text-rose-800 px-2 py-0.5 font-mono text-xs" data-testid={`recon-mismatch-${r.branch}-${r.buyer_sku_id}`}>
                            <XCircle className="w-3 h-3" strokeWidth={2.5} />
                            {Math.abs(r.delta).toLocaleString()} higher ({r.higher_side})
                          </span>
                        )}
                      </td>
                      <td className="p-3 align-middle text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDrillDown(r.buyer_sku_id, r.branch)}
                          data-testid={`recon-drilldown-${r.branch}-${r.buyer_sku_id}`}
                        >
                          <ClipboardList className="w-3.5 h-3.5 mr-1" strokeWidth={2} />
                          Audit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Drill-down dialog (AC9–AC11, AC13–AC14) ─────────────────── */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="recon-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">
              {drillContext ? `${drillContext.sku} @ ${drillContext.branch}` : 'Audit trail'}
            </DialogTitle>
            <DialogDescription>
              Chronological ledger of every event that affected this SKU/branch&apos;s pending-invoice balance.
              Rows shaded amber indicate untraced deltas — changes not recorded in either the FG movement
              ledger or the events collection.
            </DialogDescription>
          </DialogHeader>
          {drillLoading ? (
            <div className="text-center p-8 text-zinc-500 font-mono text-sm">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : !drillPayload ? (
            <div className="text-center p-8 text-zinc-500 font-mono text-sm">No data.</div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="border rounded-sm p-3 text-xs">
                  <div className="font-mono uppercase text-zinc-500">Current Stock</div>
                  <div className="text-lg font-bold tabular-nums" data-testid="drill-current-stock">
                    {Number(drillPayload.current_state.current_stock || 0).toLocaleString()}
                  </div>
                </div>
                <div className="border rounded-sm p-3 text-xs">
                  <div className="font-mono uppercase text-zinc-500">Invoiced Qty</div>
                  <div className="text-lg font-bold tabular-nums" data-testid="drill-invoiced-qty">
                    {Number(drillPayload.current_state.invoiced_qty || 0).toLocaleString()}
                  </div>
                </div>
                <div className="border rounded-sm p-3 text-xs">
                  <div className="font-mono uppercase text-zinc-500">Pending (Movement)</div>
                  <div className="text-lg font-bold tabular-nums" data-testid="drill-pending-mv">
                    {Number(drillPayload.current_state.pending_by_movement || 0).toLocaleString()}
                  </div>
                </div>
                <div className="border rounded-sm p-3 text-xs">
                  <div className="font-mono uppercase text-zinc-500">Pending (Inventory)</div>
                  <div className="text-lg font-bold tabular-nums" data-testid="drill-pending-inv">
                    {Number(drillPayload.current_state.pending_by_inventory || 0).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="border border-zinc-200 rounded-sm overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="h-9 px-3 text-left font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">When</th>
                      <th className="h-9 px-3 text-left font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Source</th>
                      <th className="h-9 px-3 text-left font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Event</th>
                      <th className="h-9 px-3 text-right font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Change</th>
                      <th className="h-9 px-3 text-right font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Before</th>
                      <th className="h-9 px-3 text-right font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">After</th>
                      <th className="h-9 px-3 text-left font-mono text-xs font-medium text-zinc-500 uppercase tracking-wider">Actor / Ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(drillPayload.entries || []).map((e, i) => (
                      <tr
                        key={i}
                        className={`border-b border-zinc-100 ${e.is_gap ? 'bg-amber-50' : 'hover:bg-zinc-50/50'}`}
                        data-testid={`drill-row-${i}`}
                      >
                        <td className="p-3 align-top text-xs font-mono text-zinc-600">
                          {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                        </td>
                        <td className="p-3 align-top text-xs font-mono">
                          {e.is_gap ? (
                            <span className="rounded-sm bg-amber-200 text-amber-900 px-1.5 py-0.5">GAP</span>
                          ) : e.source === 'event' ? (
                            <span className="rounded-sm bg-sky-100 text-sky-800 px-1.5 py-0.5">event</span>
                          ) : (
                            <span className="rounded-sm bg-zinc-100 text-zinc-800 px-1.5 py-0.5">movement</span>
                          )}
                        </td>
                        <td className="p-3 align-top text-xs font-mono">
                          <div className="font-semibold">{e.event_type || '—'}</div>
                          {e.business_event_type && (
                            <div className="text-zinc-500">{e.business_event_type}</div>
                          )}
                        </td>
                        <td className={`p-3 align-top text-right font-mono text-xs ${
                          e.is_gap ? 'text-amber-900 font-semibold'
                                   : (e.quantity_change ?? 0) > 0 ? 'text-emerald-700' : 'text-rose-700'
                        }`}>
                          {e.quantity_change == null ? '—' : (e.quantity_change > 0 ? '+' : '') + Number(e.quantity_change).toLocaleString()}
                        </td>
                        <td className="p-3 align-top text-right font-mono text-xs">
                          {e.before == null ? '—' : Number(e.before).toLocaleString()}
                        </td>
                        <td className="p-3 align-top text-right font-mono text-xs">
                          {e.after == null ? '—' : Number(e.after).toLocaleString()}
                        </td>
                        <td className="p-3 align-top text-xs font-mono text-zinc-600">
                          {e.actor && <div>{e.actor}</div>}
                          {e.reference && Object.entries(e.reference).filter(([, v]) => v != null).map(([k, v]) => (
                            <div key={k} className="text-zinc-500 text-[10px]">{k}: {String(v)}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                    {(drillPayload.entries || []).length === 0 && (
                      <tr><td colSpan={7} className="p-6 text-center text-zinc-500 font-mono text-sm">No ledger entries.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
